import {useEffect} from "react";
import { Disclosure } from "@headlessui/react";
import { useFormContext } from "react-hook-form";
import {
  METHOD_TYPES,
  DYNAMIC_BASES,
  COURIERS,
} from "../constants/index";
import PricingRulesEditor from "./PricingRulesEditor";
import { ETA_RE, ETA_UNITS } from "../constants/validation";
import { Toggle } from "../common/Toggle";
const UNIT_LABEL = {
  business_days: "business days",
  hours: "hours",
  minutes: "minutes",
};
export default function MethodCard({ zoneIndex, methodIndex, onRemove }) {
  const path = `shippingGroups.${zoneIndex}.methods.${methodIndex}`;

  const {
    register,
    watch,
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();

  const method    = watch(path);
  const methodErr = errors?.shippingGroups?.[zoneIndex]?.methods?.[methodIndex] || {};
  const hideDetails = method?.useCourierApi;

  /* helper for error highlight */
  const err = (field) => (methodErr?.[field] ? "border-red-500" : "border-gray-300");
  const etaMinVal  = watch(`${path}.etaMin`);
  const etaMaxVal  = watch(`${path}.etaMax`);
  const etaUnitVal = watch(`${path}.etaUnit`) || "business_days";

  useEffect(() => {
    if (!method?.estimatedTime) return;
    const m = method.estimatedTime.match(ETA_RE);
    if (!m) return;
    const [, a, b, unitRaw] = m;
    const unit =
      unitRaw?.toLowerCase().startsWith("minute")
        ? "minutes"
        : unitRaw?.toLowerCase().startsWith("hour")
        ? "hours"
        : "business days";

    setValue(`${path}.etaMin`, Number(a), { shouldDirty: false });
    setValue(`${path}.etaMax`, Number(b), { shouldDirty: false });
    setValue(
      `${path}.etaUnit`,
      unit === "minutes" ? "minutes" : unit === "hours" ? "hours" : "business_days",
      { shouldDirty: false }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method?.estimatedTime]);
    const syncEstimatedTime = (minMaybe, maxMaybe, unitKey = etaUnitVal) => {
    const min = Number(minMaybe);
    const max = Number(maxMaybe);
    const okMin = Number.isFinite(min) && min > 0;
    const okMax = Number.isFinite(max) && max >= min;
    const unitLabel = UNIT_LABEL[unitKey] || "business days";
    const val = okMin && okMax ? `${min}–${max} ${unitLabel}` : "";
    setValue(`${path}.estimatedTime`, val, { shouldDirty: true, shouldValidate: true });
    trigger("shippingGroups");
  };
 const useLive = watch(`${path}.useCourierApi`);
  useEffect(() => {
    trigger("shippingGroups");
    if (useLive) {
      setValue(`${path}.estimatedTime`, "", { shouldDirty: true });
      setValue(`${path}.etaMin`, undefined, { shouldDirty: true });
      setValue(`${path}.etaMax`, undefined, { shouldDirty: true });
      setValue(`${path}.rules`, undefined, { shouldDirty: true });
    }
  }, [useLive, trigger, setValue, path]);

 const handleTypeChange = (e) => {
   const t = e.target.value;
   setValue(`${path}.type`, t, { shouldDirty: true });
   if (t === "flat") {
     setValue(`${path}.rules`, { rate: undefined }, { shouldDirty: true, shouldValidate: true });
   } else if (t === "dynamic") {
     setValue(`${path}.basis`, method?.basis || "weight", { shouldDirty: true, shouldValidate: true });
     setValue(`${path}.rules`, { baseRate: undefined, ratePerKg: undefined, minCharge: undefined },
       { shouldDirty: true, shouldValidate: true });
   } else if (t === "tiered") {
     setValue(`${path}.rules`, [], { shouldDirty: true, shouldValidate: true });
   }
   trigger("shippingGroups");
 };

  const fsEnabled = watch(`${path}.freeShipping.enabled`);
  const fsMinSubtotal = watch(`${path}.freeShipping.conditions.minSubtotal`);
  const fsMinItems    = watch(`${path}.freeShipping.conditions.minItems`);
  const fsMaxWeight   = watch(`${path}.freeShipping.conditions.maxWeight`);

  const buildFsMessage = (m, i, w) => {
    const parts = [];
    if (typeof m === "number") parts.push(`orders over ${m.toFixed(2)}`);
    if (typeof i === "number") parts.push(`${i} item${i === 1 ? "" : "s"} or more`);
    if (typeof w === "number") parts.push(`total weight ≤ ${w}`);
    if (!parts.length) return "";
    return `Free shipping for ${parts.join(" and ")}`;
  };

  useEffect(() => {
    if (!fsEnabled) {
      setValue(`${path}.freeShipping.message`, "", { shouldDirty: true });
      return;
    }
    const msg = buildFsMessage(fsMinSubtotal, fsMinItems, fsMaxWeight);
    setValue(`${path}.freeShipping.message`, msg, { shouldDirty: true });
  }, [fsEnabled, fsMinSubtotal, fsMinItems, fsMaxWeight, setValue, path]);

  return (
    <Disclosure defaultOpen>
      {({ open }) => (
        <div className="mb-4 rounded border p-3 text-sm last:mb-0">
          {/* Header ----------------------------------------------------- */}
          <Disclosure.Button className="flex w-full items-center justify-between">
            <input
              {...register(`${path}.name`, { onChange: () => trigger("shippingGroups") })}
              placeholder="Method name"
              className={`flex-1 rounded border p-2 ${err("name")}`}
            />
            <span className="ml-2 text-gray-500">{open ? "▴" : "▾"}</span>
          </Disclosure.Button>
          {methodErr?.name && (
            <p className="mt-1 text-xs text-red-600">{methodErr.name.message}</p>
          )}

          {/* Body ------------------------------------------------------- */}
          <Disclosure.Panel className="mt-3 space-y-3">
            {/* Type & ETA ------------------------------------------- */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-medium">Type *</label>
                <select
                  {...register(`${path}.type`)}
                  onChange={handleTypeChange}
                  className={`mt-1 w-full rounded border p-2 ${err("type")}`}
                  disabled={hideDetails}
                >
                  {METHOD_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              {!hideDetails && (
                <div>
                  <label className="block text-xs font-medium">Estimated Time *</label>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      inputMode="numeric"
                      {...register(`${path}.etaMin`, {
                        valueAsNumber: true,
                        onChange: (e) =>
                          syncEstimatedTime(e.target.valueAsNumber, watch(`${path}.etaMax`), watch(`${path}.etaUnit`)),
                      })}
                      placeholder="min"
                      className={`w-24 rounded border p-2 text-right ${err("etaMin")}`}
                    />
                    <span className="text-gray-500">–</span>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      inputMode="numeric"
                      {...register(`${path}.etaMax`, {
                        valueAsNumber: true,
                        onChange: (e) =>
                          syncEstimatedTime(watch(`${path}.etaMin`), e.target.valueAsNumber, watch(`${path}.etaUnit`)),
                      })}
                      placeholder="max"
                      className={`w-24 rounded border p-2 text-right ${err("etaMax")}`}
                    />
                    <select
                      {...register(`${path}.etaUnit`, {
                        onChange: (e) =>
                          syncEstimatedTime(watch(`${path}.etaMin`), watch(`${path}.etaMax`), e.target.value),
                      })}
                      className="rounded border p-2 text-xs"
                    >
                      <option value="business_days">business days</option>
                      <option value="hours">hours</option>
                      <option value="minutes">minutes</option>
                    </select>
                  </div>

                  {(methodErr?.etaMin || methodErr?.etaMax) && (
                    <p className="mt-1 text-xs text-red-600">
                      {methodErr?.etaMin?.message || methodErr?.etaMax?.message}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Dynamic basis --------------------------------------- */}
            {method?.type === "dynamic" && !hideDetails && (
              <div>
                <label className="block text-xs font-medium">
                  Calculate by *
                </label>
                <select
                  {...register(`${path}.basis`)}
                  className={`mt-1 w-full rounded border p-2 ${err("basis")}`}
                >
                  {DYNAMIC_BASES.map((b) => (
                    <option key={b} value={b}>
                      {b === "weight" ? "Weight (kg)" : "Dimensions (m³)"}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Courier API toggle ----------------------------------- */}
            <div className="flex items-center gap-3">
              <span className="text-xs">Fetch live rates via courier API</span>
              <Toggle
                name={`${path}.useCourierApi`}
                control={control}
                onAfterChange={() => {
                  // make Yup re-run so it strips rules/ETA errors
                  trigger("shippingGroups");
                }}
              />
            </div>
            {/* Free shipping rules ----------------------------------- */}
            <div className="rounded border p-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium">Free shipping rules</span>
                <Toggle
                  name={`${path}.freeShipping.enabled`}
                  control={control}
                  onAfterChange={() => trigger("shippingGroups")}
                />
              </div>
              {fsEnabled && (
                <div className="mt-2 grid gap-3 sm:grid-cols-3">
                  <div>
                    <label className="block text-[11px] font-medium">Min order total</label>
                    <input
                      type="number" step="0.01" min="0"
                      {...register(`${path}.freeShipping.conditions.minSubtotal`, {
                        valueAsNumber: true,
                        onChange: () => trigger("shippingGroups"),
                      })}
                      className="mt-1 w-full rounded border p-2"
                      placeholder="e.g. 50.00"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium">Min items</label>
                    <input
                      type="number" step="1" min="1" inputMode="numeric"
                      {...register(`${path}.freeShipping.conditions.minItems`, {
                        valueAsNumber: true,
                        onChange: () => trigger("shippingGroups"),
                      })}
                      className="mt-1 w-full rounded border p-2"
                      placeholder="e.g. 3"
                    />
                  </div>

                  {(() => {
                    const condErr =
                      methodErr?.freeShipping?.conditions?.root?.message ||
                      methodErr?.freeShipping?.conditions?.message;
                    return condErr ? (
                      <p className="sm:col-span-3 mt-1 text-xs text-red-600">{condErr}</p>
                    ) : null;
                  })()}

                  <div>
                    <label className="block text-[11px] font-medium">Max total weight (kg)</label>
                    <input
                      type="number" step="0.01" min="0"
                      {...register(`${path}.freeShipping.conditions.maxWeight`, {
                        valueAsNumber: true,
                        onChange: () => trigger("shippingGroups"),
                      })}
                      className="mt-1 w-full rounded border p-2"
                      placeholder="optional"
                    />
                  </div>

                  <div className="sm:col-span-3">
                    <label className="block text-[11px] font-medium">Printed message *</label>
                    <input
                      {...register(`${path}.freeShipping.message`, {
                        onChange: () => trigger("shippingGroups"),
                      })}
                      className={`mt-1 w-full rounded border p-2 ${methodErr?.freeShipping?.message ? "border-red-500" : "border-gray-300"
                        }`}
                      placeholder="Shown to customers when the rule applies"
                    />
                    {!!methodErr?.freeShipping?.message && (
                      <p className="mt-1 text-xs text-red-600">{methodErr.freeShipping.message}</p>
                    )}
                    {/* Small live preview */}
                    <p className="mt-1 text-[11px] text-gray-500">
                      Preview: {buildFsMessage(fsMinSubtotal, fsMinItems, fsMaxWeight) || "—"}
                    </p>
                  </div>
                </div>
              )}
            </div>
            {/* Courier select (live rates) ------------------------- */}
            {method?.useCourierApi && (
              <div>
                <label className="block text-xs font-medium">Courier *</label>
                <select
                  {...register(`${path}.courier`, {
                    onChange: () => trigger("shippingGroups"), // re-run array tests (dupe courier)
                  })}
                  className={`mt-1 w-full rounded border p-2 ${err("courier")}`}
                >
                  {COURIERS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                {methodErr?.courier && (
                  <p className="mt-1 text-xs text-red-600">{methodErr.courier.message}</p>
                )}
              </div>
            )}

            {/* Pricing rules (hidden for live rates) --------------- */}
            {!hideDetails && (
              <PricingRulesEditor
                path={`${path}.rules`}
                type={method.type}
                basis={method.basis}
                errors={methodErr?.rules}
              />
            )}

            {/* Remove method --------------------------------------- */}
            <button
              type="button"
              onClick={onRemove}
              className="text-xs text-red-500 hover:underline"
            >
              Remove Method
            </button>
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  );
}
